import type { JoinRole, SeatSummary, SideBrandingSet } from '@badger-sentinel/protocol';

/**
 * The joined-but-not-yet-seated session (entry.md Open: "the session token
 * lives only in React state, so a reload between join and seat claim drops
 * back here"). Persisted the same way the seat token already is — in
 * `sessionStorage`, so it survives a reload but not a closed tab, and it is
 * never localStorage or the URL (entry.md: "the passphrase never goes in the
 * URL or localStorage" — the token this stores is not the passphrase, but the
 * same bar applies).
 */

export interface JoinedSession {
  readonly sessionToken: string;
  readonly gameId: string;
  readonly code: string;
  readonly role: JoinRole;
  readonly displayName: string | null;
  /** The roster as of join. Seat select re-reads it; this is only a fast first paint. */
  readonly seats: readonly SeatSummary[];
  /** Names this game's scenario art directory. Known only after the passphrase. */
  readonly scenario: string;
  /** What this scenario calls the three sides — resolved by the server. */
  readonly sides: SideBrandingSet;
}

const STORAGE_KEY = 'badger-sentinel.session';

export function readStoredSession(): JoinedSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<JoinedSession> | null;
    // A session written by a previous build of this client is not a session this
    // one can render — dropping it costs a passphrase, using it costs a blank
    // header and an undefined lookup three renders later.
    if (!parsed?.sessionToken || !parsed.gameId || !parsed.scenario || !parsed.sides) return null;
    return parsed as JoinedSession;
  } catch {
    return null;
  }
}

export function storeSession(session: JoinedSession | null): void {
  try {
    if (session) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private browsing; the session simply will not survive a reload */
  }
}

/**
 * A claimed seat — the credential everything past seat select runs on. Kept in
 * `sessionStorage` so a reload resumes the seat without a passphrase
 * (entry.md: "a returning player skips this... reloading mid-turn must not
 * cost a passphrase").
 */
export interface ClaimedSeat {
  readonly gameId: string;
  readonly seatToken: string;
  readonly cell: string;
  /**
   * The scenario's name and its side branding, carried across the claim.
   *
   * **Not duplication — the only route these have to a reloaded seat.**
   * `session.hello.seat` (`SeatIdentity`) carries the cell, the team, the epoch
   * and the display name, and nothing else: no side name, no unit table, no
   * scenario. Without these two fields a seat that reloads mid-turn draws an
   * identity row with no command name, no unit and no patch, because
   * `imagery.tsx` keys art on the scenario. That is exactly the silent default
   * M5-D10 forbids.
   *
   * The alternative was to let `GET /api/games/:gameId` accept a seat token.
   * It was rejected: that route answers with a whole `GameSummary` — the full
   * roster with everybody's display name, the game config, the content hash —
   * which is a great deal more than two strings, and it would put a second
   * authentication path on a handler written for one. Branding is fixed for
   * the life of a game (the scenario is chosen at creation and `sides` is
   * resolved from it once), the client already holds both at claim time, and
   * `JoinedSession` already keeps them in this same storage for the same
   * lifetime — so the bar was already set here.
   */
  readonly scenario: string;
  readonly sides: SideBrandingSet;
}

const SEAT_STORAGE_KEY = 'badger-sentinel.seat';

export function readStoredSeat(): ClaimedSeat | null {
  try {
    const raw = sessionStorage.getItem(SEAT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ClaimedSeat> | null;
    // A seat written by a previous build has no branding, and a header with no
    // side name is worse than a passphrase — same reasoning as `readStoredSession`.
    if (!parsed?.gameId || !parsed.seatToken || !parsed.cell || !parsed.scenario || !parsed.sides) {
      return null;
    }
    return parsed as ClaimedSeat;
  } catch {
    return null;
  }
}

export function storeSeat(seat: ClaimedSeat | null): void {
  try {
    if (seat) sessionStorage.setItem(SEAT_STORAGE_KEY, JSON.stringify(seat));
    else sessionStorage.removeItem(SEAT_STORAGE_KEY);
  } catch {
    /* private browsing; the seat simply will not survive a reload */
  }
}

/**
 * A seat that has been **claimed but not entered** — the window between
 * `TAKE SEAT` and `ENTER AS …`, which is exactly where a cadet reads the cell
 * briefing.
 *
 * Separate from `ClaimedSeat` because the two mean different things to `App`:
 * a stored `ClaimedSeat` mounts the console, and writing one here would send a
 * reloading player straight past the roster into a seat they had not entered
 * yet. What this holds is the token needed to take that seat *back*.
 *
 * Without it, a reload in that window stranded the player outside their own
 * seat: `take()` set React state and nothing else, the server had already held
 * the seat on its 120 s grace window, and the restored roster therefore showed
 * their own cell as held by their own name with a disabled `HELD` button and no
 * way back in. `docs/frontend/pages/seat-select.md` names the fix — "a
 * self-reclaim is not a takeover" — and `claimSeat`'s `reclaimToken` is what
 * performs it: presenting the previous seat token proves the epoch is yours.
 */
export interface PendingSeat {
  readonly gameId: string;
  readonly cell: string;
  /** The previous seat token, presented back as `reclaimToken`. */
  readonly seatToken: string;
  readonly epoch: number;
}

const PENDING_SEAT_KEY = 'badger-sentinel.pending-seat';

export function readPendingSeat(): PendingSeat | null {
  try {
    const raw = sessionStorage.getItem(PENDING_SEAT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingSeat> | null;
    if (!parsed?.gameId || !parsed.cell || !parsed.seatToken || parsed.epoch === undefined) {
      return null;
    }
    return parsed as PendingSeat;
  } catch {
    return null;
  }
}

export function storePendingSeat(seat: PendingSeat | null): void {
  try {
    if (seat) sessionStorage.setItem(PENDING_SEAT_KEY, JSON.stringify(seat));
    else sessionStorage.removeItem(PENDING_SEAT_KEY);
  } catch {
    /* private browsing; a reload in this window will cost the grace period */
  }
}
